import type { AdminAssessmentReportState } from '@nestjs-boilerplate/types';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Download } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { downloadAdminReportPdf, useAdminAssessment } from '../api/assessments';

/**
 * What each report state means, in the terms an administrator cares about:
 * whether the report exists, and if not, why not.
 *
 * Four states rather than a link that fails. Most rows on this platform no
 * longer have a report, and saying which of the four situations a row is in is
 * more use than offering a download that errors.
 */
const REPORT_STATES: Record<AdminAssessmentReportState, string> = {
  available: 'Available — within the download window',
  expired: 'Expired — the download window has lapsed and can be refreshed by the owner',
  purged: 'Purged — removed by the retention policy and not recoverable',
  never_generated: 'Never generated — the screening did not produce a report',
};

/**
 * One assessment, summarised for an administrator, with the report offered as
 * a download when there still is one.
 *
 * A download rather than an inline render. The report is a deliverable with an
 * expiry, not a page of text, and rendering its markdown here put its
 * formatting in two places — which had already produced one defect. The
 * download goes through the admin endpoint, which applies retention but not
 * ownership or purchase, and records every download in the activity log.
 *
 * Most rows no longer have a report at all, so the summary always states which
 * of the four report states applies, with or without a button to follow.
 */
export function AssessmentDetailPage() {
  const { id } = useParams({ from: '/admin/assessments/$id' });
  const { data, isLoading, isError, error } = useAdminAssessment(id);

  return (
    <div className="space-y-6">
      <Link
        to="/admin/assessments"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:underline"
      >
        <ArrowLeft className="size-4" />
        Back to assessments
      </Link>

      {isError ? (
        <Card>
          <CardContent className="pt-6">
            {/* A 404 here means the id is not an assessment, which is an answer
                rather than a fault. Sending it to the error boundary would tell
                the reader the application broke. */}
            <p className="text-sm text-destructive">
              {isNotFound(error)
                ? 'No assessment with that reference. It may have been permanently removed.'
                : `Could not load this assessment${error instanceof Error ? `: ${error.message}` : '.'}`}
            </p>
          </CardContent>
        </Card>
      ) : isLoading || !data ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </CardContent>
        </Card>
      ) : (
        <>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">{data.optimalKycRef}</h2>
            <p className="text-sm text-muted-foreground">Client reference {data.clientRef}</p>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
                  <Field label="Client">
                    {data.purged ? (
                      <span className="text-muted-foreground italic">purged</span>
                    ) : (
                      data.clientName
                    )}
                  </Field>
                  <Field label="Client email">
                    {data.clientEmail ?? <Absent>none recorded</Absent>}
                  </Field>
                  <Field label="Risk level">{data.riskLevel}</Field>
                  <Field label="Risk score">
                    {data.riskScore !== null ? data.riskScore : <Absent>not scored</Absent>}
                  </Field>
                  <Field label="Status">{data.status}</Field>
                  <Field label="Screening job">{data.screeningJobStatus}</Field>
                  <Field label="Legal matter">{data.isLegalMatter ? 'Yes' : 'No'}</Field>
                  {/* Shown because it changes what deleting this row means: a
                      fixture is served in place of live screening for anyone
                      using its client reference, so removing one breaks demos
                      rather than tidying up an old case. */}
                  <Field label="Demo fixture">
                    {data.isDemoFixture ? 'Yes — served in place of live screening' : 'No'}
                  </Field>
                  <Field label="Checks run">
                    {data.enabledChecks.length > 0 ? (
                      data.enabledChecks.join(', ')
                    ) : (
                      <Absent>none recorded</Absent>
                    )}
                  </Field>
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Owner</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
                  <Field label="Email">{data.ownerEmail}</Field>
                  <Field label="Name">{data.ownerName ?? <Absent>not provided</Absent>}</Field>
                  <Field label="Company">{data.ownerCompany ?? <Absent>none linked</Absent>}</Field>
                  <Field label="User id">
                    <span className="font-mono text-xs">{data.ownerId}</span>
                  </Field>
                </dl>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm font-medium">Report</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
                  <Field label="State">{REPORT_STATES[data.reportState]}</Field>
                  <Field label="Generated">
                    <Timestamp value={data.dateReportGenerated} absent="never" />
                  </Field>
                  <Field label="Window ends">
                    <Timestamp value={data.reportExpiresAt} absent="no window" />
                  </Field>
                  <Field label="Refreshes">{data.refreshCount}</Field>
                  <Field label="Purged">
                    <Timestamp value={data.purgedAt} absent="not purged" />
                  </Field>
                  <Field label="Created">
                    <Timestamp value={data.createdAt} absent="unknown" />
                  </Field>
                  <Field label="Last updated">
                    <Timestamp value={data.updatedAt} absent="unknown" />
                  </Field>
                  <Field label="Deleted">
                    <Timestamp value={data.dateDeleted} absent="not deleted" />
                  </Field>
                </dl>
              </CardContent>
            </Card>

            {data.reportState === 'available' ? (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Generated report</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Only when the report is actually there. For every other
                      state the field above already says why there is nothing to
                      download, which is more use than a button that errors. */}
                  <ReportDownload id={data.id} />
                </CardContent>
              </Card>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The download button, and whatever the server said if it refused.
 *
 * Local state rather than a mutation hook: nothing is being changed, so there
 * is no cache to invalidate and no other component to notify. The failure worth
 * showing is the window having lapsed between this page loading and the button
 * being pressed, which the server's own message says better than a generic one.
 */
function ReportDownload({ id }: { id: string }) {
  const [downloading, setDownloading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function download() {
    setDownloading(true);
    setFailure(null);
    try {
      await downloadAdminReportPdf(id);
    } catch (err) {
      setFailure(err instanceof Error ? err.message : 'Could not download this report');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-2">
      <Button onClick={download} disabled={downloading}>
        <Download className="mr-2 size-4" />
        {downloading ? 'Preparing PDF…' : 'Download report PDF'}
      </Button>
      <p className="text-sm text-muted-foreground">
        Downloads are recorded against your account in the activity log.
      </p>
      {failure ? <p className="text-sm text-destructive">{failure}</p> : null}
    </div>
  );
}

/** One label-and-value pair inside a summary list. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/**
 * A value that is legitimately absent, said in words. A blank cell reads as a
 * rendering failure; "none recorded" is an answer.
 */
function Absent({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground italic">{children}</span>;
}

/** A timestamp, or an explanation of why there isn't one. */
function Timestamp({ value, absent }: { value: Date | string | null; absent: string }) {
  if (!value) {
    return <Absent>{absent}</Absent>;
  }
  return <>{new Date(value).toLocaleString()}</>;
}

/**
 * Whether a failure is the endpoint saying there is no such assessment.
 *
 * Matched on the message rather than a status code because apiClient surfaces
 * failures as plain Errors. The endpoint answers a malformed id with a 404 as
 * well, which is the same answer from the reader's point of view.
 */
function isNotFound(error: unknown): boolean {
  return error instanceof Error && /not found/i.test(error.message);
}
